import { AIText } from "@/components/AIText";
import { useAIAvailability } from "@/lib/aiAvailability";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { brl, num } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Boxes, AlertTriangle, TrendingDown, ShoppingCart, Ship, Activity, DollarSign, Package, ArrowRight,
  FileDown, CheckCircle2, PackageX, Gauge, Sparkles,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, LineChart, Line,
} from "recharts";

const PIE_COLORS = ["#1B5FAA", "#38BDF8", "#E8362C", "#F59E0B", "#10B981", "#A78BFA"];

function Kpi({ icon: Icon, label, value, sub, accent, testid, onClick }) {
  return (
    <Card
      data-testid={testid}
      onClick={onClick}
      className={`card-metal p-5 relative overflow-hidden ${onClick ? "cursor-pointer" : ""} animate-in-up`}
    >
      <div className={`absolute -right-6 -top-6 h-24 w-24 rounded-full blur-2xl opacity-20 ${accent}`} />
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-400 font-mono">{label}</div>
          <div className="mt-2 font-display text-3xl font-bold text-slate-50">{value}</div>
          {sub && <div className="mt-1 text-xs text-slate-400">{sub}</div>}
        </div>
        <div className={`rounded-lg p-2.5 ${accent} bg-opacity-20`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
    </Card>
  );
}

const ChartBox = ({ title, children }) => (
  <Card className="card-metal p-5">
    <h3 className="font-display text-lg font-semibold text-slate-100 mb-4">{title}</h3>
    {children}
  </Card>
);

const tooltipStyle = { background: "#0F172A", border: "1px solid #334155", borderRadius: 8, color: "#F8FAFC" };

export default function Dashboard() {
  const [data, setData] = useState(null);
  const { canFinance, hasModule, user } = useAuth();
  const ai = useAIAvailability(hasModule("ia"));
  const navigate = useNavigate();
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const genSummary = async () => {
    if (!ai.available || aiBusy) return;
    setAiBusy(true); setAiText(""); setAiOpen(true);
    try {
      const { data: r } = await api.post("/ai/dashboard-summary", { kpis: data.kpis, atencao: data.atencao || [] });
      setAiText(r.summary);
    } catch (error) {
      if (error?.response?.data?.detail?.code === "AI_TEMPORARILY_UNAVAILABLE") {
        ai.markUnavailable(); setAiOpen(false); return;
      }
      setAiText(error?.response?.data?.detail?.message || "Não foi possível gerar o resumo agora. Tente novamente."); }
    finally { setAiBusy(false); }
  };

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const exportPDF = async () => {
    let purchases = [];
    if (hasModule("compras")) {
      try { const r = await api.get("/purchases/recommended"); purchases = r.data.filter((p) => p.metrics.quantidade_sugerida > 0); } catch {}
    }
    const k = data.kpis;
    const rows = purchases.slice(0, 40).map((p) => `<tr><td>${p.descricao}</td><td>${p.codigo_interno}</td><td style="text-align:right">${num(p.quantidade_atual)}</td><td style="text-align:right">${p.metrics.quantidade_sugerida}</td><td style="text-align:right">${p.metrics.custo_estimado != null ? brl(p.metrics.custo_estimado) : "—"}</td></tr>`).join("");
    const fin = data.finance;
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Relatório Peça360</title><style>
      body{font-family:Arial,sans-serif;color:#0f172a;padding:32px}h1{color:#0F4C81;margin-bottom:4px}
      .sub{color:#64748b;margin-top:0}.kpis{display:flex;flex-wrap:wrap;gap:12px;margin:20px 0}
      .kpi{border:1px solid #e2e8f0;border-radius:10px;padding:12px 16px;min-width:150px}
      .kpi .l{font-size:11px;text-transform:uppercase;color:#64748b}.kpi .v{font-size:22px;font-weight:bold}
      table{width:100%;border-collapse:collapse;margin-top:12px;font-size:12px}th,td{border-bottom:1px solid #e2e8f0;padding:6px 8px;text-align:left}
      th{background:#f1f5f9;text-transform:uppercase;font-size:10px}</style></head><body>
      <h1>Peça360 · Relatório para a Diretoria</h1>
      <p class="sub">${user?.tenant_name || "Empresa"} · ${new Date().toLocaleDateString("pt-BR")} ${new Date().toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"})}</p>
      <div class="kpis">
        ${fin ? `<div class="kpi"><div class="l">Valor total do estoque</div><div class="v">${brl(k.valor_total_estoque)}</div></div>` : ""}
        <div class="kpi"><div class="l">Total de peças</div><div class="v">${k.total_pecas}</div></div>
        <div class="kpi"><div class="l">Peças críticas</div><div class="v">${k.pecas_criticas}</div></div>
        <div class="kpi"><div class="l">Risco de ruptura</div><div class="v">${k.risco_ruptura}</div></div>
        <div class="kpi"><div class="l">Compras recomendadas</div><div class="v">${k.compras_recomendadas}${fin ? ` · ${brl(k.valor_compras_recomendadas)}` : ""}</div></div>
        <div class="kpi"><div class="l">Próxima importação</div><div class="v">${k.itens_proxima_importacao} itens</div></div>
      </div>
      <h3>Compras recomendadas</h3>
      <table><thead><tr><th>Peça</th><th>Código</th><th style="text-align:right">Estoque</th><th style="text-align:right">Sugerido</th><th style="text-align:right">Custo est.</th></tr></thead><tbody>${rows || '<tr><td colspan="5">Sem itens</td></tr>'}</tbody></table>
      <script>window.onload=()=>window.print()</script></body></html>`);
    w.document.close();
  };

  if (!data)
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64 bg-slate-800" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-28 bg-slate-800" />)}
        </div>
      </div>
    );

  const k = data.kpis;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-50">Dashboard Executivo</h1>
          <p className="text-slate-400 text-sm">Visão geral do almoxarifado de {user?.tenant_name || "sua empresa"} — atualizado em tempo real.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {hasModule("ia") && (
            <Button data-testid="btn-ai-summary" onClick={genSummary} disabled={!ai.available || aiBusy} className="bg-gradient-to-r from-sky-600 to-indigo-600 hover:from-sky-500 hover:to-indigo-500 text-white">
              <Sparkles className="h-4 w-4 mr-1" /> {aiBusy ? "Gerando..." : "Resumo Executivo (IA)"}
            </Button>
          )}
          <Button data-testid="btn-export-pdf" variant="secondary" onClick={exportPDF} className="bg-sky-900/40 hover:bg-sky-800/50 text-sky-100">
            <FileDown className="h-4 w-4 mr-1" /> Relatório da Diretoria
          </Button>
        </div>
      </div>

      {hasModule("ia") && !ai.available && <p role="status" data-testid="ai-unavailable" className="text-sm text-amber-200">{ai.message}</p>}

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Sparkles className="h-6 w-6 text-sky-400" /> Resumo Executivo (IA)</DialogTitle></DialogHeader>
          <div data-testid="ai-summary-text" className="text-sm text-slate-200 leading-relaxed min-h-[80px]">{aiBusy && !aiText ? "Gerando resumo..." : <AIText text={aiText} />}</div>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {canFinance ? (
          <>
            <Kpi testid="kpi-total-inventory-value" icon={DollarSign} label="Valor total do estoque" value={brl(k.valor_total_estoque)} accent="bg-sky-600" />
            {hasModule("compras") && <Kpi testid="kpi-recommended-purchases" icon={ShoppingCart} label="Compras recomendadas" value={num(k.compras_recomendadas)} sub={brl(k.valor_compras_recomendadas)} accent="bg-amber-600" onClick={() => navigate("/compras")} />}
          </>
        ) : (
          <>
            <Kpi testid="kpi-coverage-avg" icon={Gauge} label="Cobertura média" value={`${num(k.cobertura_media_dias)} dias`} sub="autonomia média do estoque" accent="bg-sky-600" />
            <Kpi testid="kpi-out-of-stock" icon={PackageX} label="Peças sem estoque" value={num(k.pecas_sem_estoque)} accent="bg-rose-700" onClick={() => navigate("/pecas")} />
          </>
        )}
        <Kpi testid="kpi-total-parts" icon={Package} label="Total de peças" value={num(k.total_pecas)} sub="peças ativas cadastradas" accent="bg-slate-500" onClick={() => navigate("/pecas")} />
        <Kpi testid="kpi-critical-items-count" icon={AlertTriangle} label="Peças críticas" value={num(k.pecas_criticas)} accent="bg-rose-600" onClick={() => navigate("/pecas")} />
        <Kpi testid="kpi-rupture-risk" icon={TrendingDown} label="Risco de ruptura" value={num(k.risco_ruptura)} accent="bg-orange-600" onClick={() => navigate("/alertas")} />
        {hasModule("importacoes") && <Kpi testid="kpi-next-import" icon={Ship} label="Próxima importação" value={num(k.itens_proxima_importacao)} sub="itens importados a incluir" accent="bg-rose-500" onClick={() => navigate("/importacoes")} />}
        {hasModule("movimentacoes") && <Kpi testid="kpi-movements-month" icon={Activity} label="Movimentações do mês" value={num(k.movimentacoes_mes)} accent="bg-emerald-600" onClick={() => navigate("/movimentacoes")} />}
        {canFinance ? (
          hasModule("compras") ? <Kpi testid="kpi-purchase-coverage" icon={Boxes} label="Cobertura de compras" value={brl(k.valor_compras_recomendadas)} sub="estimativa próximo ciclo" accent="bg-sky-700" onClick={() => navigate("/compras")} /> : null
        ) : (
          <Kpi testid="kpi-adequate" icon={CheckCircle2} label="Peças adequadas" value={num(k.pecas_adequadas)} sub="dentro do nível ideal" accent="bg-emerald-700" onClick={() => navigate("/pecas")} />
        )}
      </div>

      {/* Attention area */}
      {data.atencao_necessaria?.length > 0 && (
        <Card data-testid="attention-area" className="card-metal p-5 border-amber-800/40">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-5 w-5 text-amber-400" />
            <h3 className="font-display text-lg font-semibold text-amber-200">Atenção necessária</h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {data.atencao_necessaria.map((a, i) => (
              <div key={i} className="flex items-start gap-2 rounded-lg bg-amber-950/30 border border-amber-900/40 px-3 py-2 text-sm text-amber-100">
                <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0" /> {a}
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ChartBox title="Consumo mensal (últimos 6 meses)">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.graficos.consumo_mensal}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
                <XAxis dataKey="mes" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(56,189,248,0.08)" }} />
                <Bar dataKey="consumo" fill="#1B5FAA" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartBox>
        </div>
        <ChartBox title="Categorias mais consumidas">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={data.graficos.categorias_consumo} dataKey="consumo" nameKey="categoria" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={2}>
                {data.graficos.categorias_consumo.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 space-y-1">
            {data.graficos.categorias_consumo.slice(0, 5).map((c, i) => (
              <div key={i} className="flex items-center justify-between text-xs text-slate-400">
                <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />{c.categoria}</span>
                <span className="font-mono text-slate-300">{c.consumo}</span>
              </div>
            ))}
          </div>
        </ChartBox>
      </div>

      <ChartBox title="Consumo por máquina (últimos 6 meses)">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data.graficos.consumo_por_maquina} layout="vertical" margin={{ left: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" horizontal={false} />
            <XAxis type="number" stroke="#64748B" fontSize={12} />
            <YAxis type="category" dataKey="maquina" stroke="#64748B" fontSize={12} width={120} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(232,54,44,0.08)" }} />
            <Bar dataKey="consumo" fill="#E8362C" radius={[0, 6, 6, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartBox>
    </div>
  );
}
